import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useBatches, type BatchFilters, type BatchSummary } from "../hooks/useBatches";
import { usePoll } from "../../../hooks/usePoll";
import { BulkActionBar } from "../components/BulkActionBar";
import { UploadDialog } from "../components/UploadDialog";
import {
  BatchStatusBadge,
  BatchStageBreakdown,
  workItemStatus,
} from "../components/BatchStatusBadge";
import { EmptyState } from "../../../components/EmptyState";
import { api } from "../../../lib/api-client";
import {
  formatBatchDisplayId,
  hasStructuredDisplayId,
  renderBatchLabel,
} from "../../../lib/formatters";
import { useTenantDisplayTemplates } from "../../../hooks/useTenantDisplayTemplates";
import { useQueueCounts } from "../../../hooks/useQueueCounts";
import { useWsClient } from "../../../lib/ws-provider";
// R-147 — Current Step column reuses the shared stage-label formatter so
// the BatchListPage matches the spelling used in StageCard / PipelineTab.
// DE-1545 — the canonical lifecycle label map keeps work-item wording in sync
// with the Queue Dashboard / Pending Release surfaces.
import { formatStageLabel, REMITTANCE_STEP_TYPES } from "@capture/shared";
import "../batches.css";

const PAGE_SIZE = 25;

const STATUS_OPTIONS = [
  { value: "all", label: "All Statuses" },
  { value: "uploading", label: "Uploading" },
  { value: "processing", label: "Processing" },
  { value: "balance_review", label: "Balance Review" },
  { value: "reconciliation_failed", label: "Reconciliation Failed" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "needs_header_review", label: "Needs Header Review" },
];

// R-147 — Current Step filter options. Mirrors the pipeline step types
// declared in prisma/schema.prisma and used by the workflow engine. Kept
// here (not in @capture/shared) because the existing Status / Job Type
// dropdowns also keep their option lists inline — same pattern.
const CURRENT_STEP_OPTIONS = [
  { value: "all", label: "All Steps" },
  { value: "scan", label: "Scan" },
  { value: "classify", label: "Classify" },
  { value: "key", label: "Key" },
  { value: "verify", label: "Verify" },
  // F-CAPTURE-008 — remittance pipelines (scan > classify > remittance > export)
  // put batches on these steps, and the filter could not reach them.
  ...REMITTANCE_STEP_TYPES.map((value) => ({ value, label: formatStageLabel(value) })),
  { value: "qc", label: "QC" },
  { value: "supervisor", label: "Supervisor" },
  { value: "escalation", label: "Escalation" },
  { value: "export", label: "Export" },
  { value: "none", label: "(All Completed)" },
];

// R-147 — same canonical order the API uses to break ties. Drives the
// "Current Step" column sort so steps land in pipeline order, not the
// alphabetical order of their labels (Classify < Export < Key < Scan…
// would be useless to a keyer).
const PIPELINE_STEP_RANK: Record<string, number> = {
  scan: 0,
  classify: 1,
  key: 2,
  verify: 3,
  // F-CAPTURE-008 — a remittance pipeline runs these between classify and
  // export (combined `remittance`, or split key → balance). Unranked, they
  // sorted after Export.
  remittance: 4,
  "remittance-key": 4,
  "remittance-balance": 5,
  qc: 6,
  supervisor: 7,
  escalation: 8,
  export: 9,
};

type CurrentStepSortDir = "asc" | "desc" | null;

// F-CAPTURE-009 — columns the API can order (server-side, across every page).
// Progress and Current Step are derived on read, so they are not in this set.
type ServerSort = { key: string; dir: "asc" | "desc" } | null;

function nextSort(current: ServerSort, key: string): ServerSort {
  if (current?.key !== key) return { key, dir: "asc" };
  return current.dir === "asc" ? { key, dir: "desc" } : null;
}

function SortableHeader({ label, sortKey, sort, onSort }: {
  label: string;
  sortKey: string;
  sort: ServerSort;
  onSort: (key: string) => void;
}) {
  const dir = sort?.key === sortKey ? sort.dir : null;
  return (
    <th aria-sort={dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none"}>
      <button type="button" className="batch-th-sort-button" onClick={() => onSort(sortKey)}>
        {label}
        {dir && <span className="batch-th-sort-indicator" aria-hidden="true">{dir === "asc" ? "▲" : "▼"}</span>}
      </button>
    </th>
  );
}

// R-214 / F-074 — workItemStatus (the WORKABILITY axis) moved to
// components/BatchStatusBadge.tsx (F-085) so BatchDetailPage can compose the
// same "<state> — <workability>" header phrase; imported above.

// F-047 / F-065 / F-094 — the ?state= deep-link predicate ("batch contains
// ≥1 doc in that state") now lives SERVER-SIDE in GET /api/batches?state=
// (apps/api/src/routes/batches — filtered before limit/offset, filtered
// total), so pagination over a state deep link is honest. This page just
// passes the param through via `filters.state`.

function formatRelativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function BatchListPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search") ?? "";
  // R-214 — honor the Queue Dashboard deep-link params (?jobType= / ?step=) so a
  // lane click lands here pre-filtered. Computed eagerly (same pattern as
  // initialSearch) for the initial filter state; the selects own them after.
  // jobType is the server-side filter; step is the client-side current-step filter.
  const initialJobType = searchParams.get("jobType") ?? "";
  const initialStep = searchParams.get("step") ?? "all";
  // F-047 / F-094 — terminal-lane (Needs attention / Complete) deep-link: the
  // Queue Dashboard passes ?state=error|complete so the click lands here
  // filtered to batches that actually contain docs in that state, not the
  // whole job-type list. Server-side since F-094 (passed through as a query
  // param so the filtered total makes pagination honest).
  const initialState = searchParams.get("state") ?? "all";
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobTypeFilter, setJobTypeFilter] = useState(initialJobType);
  const [jobTypes, setJobTypes] = useState<{ id: string; name: string }[]>([]);
  const [search, setSearch] = useState(initialSearch);
  const [page, setPage] = useState(0);
  // F-CAPTURE-010 — the header search box navigates to ?search=. When this page
  // is already mounted, that changes the URL but not the seeded `search` state, so
  // Enter in the header did nothing here. Follow the URL when IT changes; the
  // in-page box still owns typing (it never writes the URL, so no loop).
  const urlSearch = searchParams.get("search") ?? "";
  useEffect(() => {
    setSearch(urlSearch);
    setPage(0);
  }, [urlSearch]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // R-133 — keyer-facing filter: when on, show only batches whose
  // derived aggregateStatus is "Ready for Keying". Independent of the
  // legacy status filter (which targets the raw Batch.status column).
  const [readyOnly, setReadyOnly] = useState(false);
  // R-147 — Current Step filter + client-side sort. Filter is applied
  // client-side (same as Ready-for-Keying) because `currentStep` is a
  // derived field — the API doesn't accept it as a query param.
  const [currentStepFilter, setCurrentStepFilter] = useState(initialStep);
  const [stateFilter] = useState(initialState);
  const [currentStepSort, setCurrentStepSort] = useState<CurrentStepSortDir>(null);
  const [serverSort, setServerSort] = useState<ServerSort>(null);
  // F-CAPTURE-020 — the default list is ACTIVE batches (the API leaves out batches
  // whose every document is terminal). Say so, and let the operator include them.
  const [showCompleted, setShowCompleted] = useState(false);
  // One ordering at a time: a column sort replaces the Current Step sort.
  const onSort = (key: string) => {
    setServerSort((s) => nextSort(s, key));
    setCurrentStepSort(null);
    setPage(0);
  };

  // R-214 — the "M documents" in the list header reconciles to the sidebar
  // badge's documents-in-process (same source, same WS client) by construction.
  const wsClient = useWsClient();
  const { totalCount: documentsInProcess } = useQueueCounts(wsClient);

  useEffect(() => {
    api.get<{ items: { id: string; name: string }[] }>("/job-types?limit=100")
      .then((data) => setJobTypes(data.items))
      .catch(() => {});
  }, []);

  const filters: BatchFilters = useMemo(
    () => ({
      status: statusFilter !== "all" ? statusFilter : undefined,
      jobTypeId: jobTypeFilter || undefined,
      search: search || undefined,
      // F-065 — a ?state= deep link must see BOTH mixed (active) and fully-
      // complete (terminal) batches, but the API's default scope excludes
      // fully-terminal ones. Widen to scope=all only while the state filter
      // is active; otherwise leave the default active-only behavior alone.
      // F-CAPTURE-020 — or when the operator asks to include completed batches.
      scope: stateFilter !== "all" || showCompleted ? "all" : undefined,
      // F-094 — the state predicate runs server-side (before limit/offset)
      // so the returned page AND total are already filtered.
      state: stateFilter !== "all" ? stateFilter : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      sort: serverSort?.key,
      dir: serverSort?.dir,
    }),
    [statusFilter, jobTypeFilter, search, stateFilter, page, serverSort, showCompleted],
  );

  const { items, total, isLoading, isRefreshing, error, refresh } = useBatches(filters, wsClient);
  // R-182 — initial-load skeleton only. `isLoading` is one-way (true until the
  // first fetch resolves, never back to true on a refetch), so gating on
  // `isLoading && items.length === 0` shows the spinner ONLY on the very first
  // load. Background polls keep the table mounted; `isRefreshing` drives a
  // subtle non-blocking indicator instead.
  const showInitialSkeleton = isLoading && items.length === 0;
  // Live-batch-surfaces Task 3 — primary freshness is now the WS refetch
  // inside useBatches (batch_ingested / batch_released / queue_update /
  // document_advance / export_status on queue:<tenantId>). The poll is a
  // reconnect safety net only (mirrors QueueDashboardPage), so it runs at
  // 45s instead of R-159's 15s. Still paused when the tab is hidden; still
  // fires immediately on tab return.
  usePoll(refresh, 45_000);
  const { hasAnyTemplate } = useTenantDisplayTemplates();
  const totalPages = Math.ceil(total / PAGE_SIZE);

  // R-133 — apply Ready-for-Keying filter + readiness sort on the client.
  // Server-side `status` filter targets Batch.status (lifecycle), but
  // aggregateStatus is derived per-request so filtering by it lives here.
  // Within the displayed page, surface Ready batches first so the next
  // keyable batch is at the top — that's the whole point of the badge.
  // R-147 — also applies the Current Step filter and (when the user has
  // clicked the column header) the per-step sort, which overrides the
  // Ready-first ordering so the operator gets exactly what they asked for.
  const visibleItems = useMemo<BatchSummary[]>(() => {
    let filtered = readyOnly
      ? items.filter((b) => b.aggregateStatus === "Ready for Keying")
      : items;

    // R-147 — Current Step filter. "none" matches batches with no active
    // step (all docs exported); any other value matches batches whose
    // primary step equals that value.
    if (currentStepFilter !== "all") {
      filtered = filtered.filter((b) => {
        const primary = b.currentStep?.primary ?? null;
        return currentStepFilter === "none"
          ? primary === null
          : primary === currentStepFilter;
      });
    }

    // F-094 — the ?state= filter is applied SERVER-SIDE (filters.state);
    // re-filtering the page here would reintroduce the pagination lie.

    if (currentStepSort !== null) {
      // Explicit user sort takes precedence. Batches with no active step
      // (primary === null) always sort to the end regardless of direction
      // so the operator sees actionable rows first.
      const dir = currentStepSort === "asc" ? 1 : -1;
      return [...filtered].sort((a, b) => {
        const ap = a.currentStep?.primary ?? null;
        const bp = b.currentStep?.primary ?? null;
        if (ap === null && bp === null) return 0;
        if (ap === null) return 1;
        if (bp === null) return -1;
        const ar = PIPELINE_STEP_RANK[ap] ?? Number.MAX_SAFE_INTEGER;
        const br = PIPELINE_STEP_RANK[bp] ?? Number.MAX_SAFE_INTEGER;
        if (ar !== br) return (ar - br) * dir;
        return ap.localeCompare(bp) * dir;
      });
    }

    // Default — stable sort: Ready first, then everything else in the
    // API's original (createdAt desc) order.
    return [...filtered].sort((a, b) => {
      const aReady = a.aggregateStatus === "Ready for Keying" ? 0 : 1;
      const bReady = b.aggregateStatus === "Ready for Keying" ? 0 : 1;
      return aReady - bReady;
    });
  }, [items, readyOnly, currentStepFilter, currentStepSort]);
  const readyCount = useMemo(
    () => items.filter((b) => b.aggregateStatus === "Ready for Keying").length,
    [items],
  );

  return (
    <div className="admin-page form-default">
      <div className="admin-header">
        <div className="batch-title-block">
          <h1 className="admin-title">Batches</h1>
          {/* F-047 / F-CAPTURE-020 — two different scopes, each named: `total` is the
              batches this list shows (ACTIVE by default — batches whose every document
              is terminal are left out unless "Show completed batches" is ticked), and
              `documentsInProcess` counts DOCUMENTS (same source as the sidebar). */}
          <div className="batch-totals" data-testid="batches-totals">
            {total} {showCompleted || stateFilter !== "all" ? "" : "active "}{total === 1 ? "batch" : "batches"}
            {" · "}
            {documentsInProcess} {documentsInProcess === 1 ? "document" : "documents"} in process
          </div>
          <label className="batch-show-completed">
            <input
              type="checkbox"
              checked={showCompleted}
              onChange={(e) => {
                setShowCompleted(e.target.checked);
                setPage(0);
              }}
            />
            Show completed batches
          </label>
        </div>
        {/* Hidden while empty so EmptyState's CTA is the single entry point (#48/#78 family). */}
        {items.length > 0 && (
          <button
            className="batch-upload-btn"
            onClick={() => setUploadOpen(true)}
          >
            Upload
          </button>
        )}
        <div className="batch-header-actions">
          {/* R-133 — Ready-for-Keying filter chip. Toggle button so a keyer
              can flip into "show me only what's pickable right now" with a
              single click. Shows a live count badge so even when off the
              keyer sees how many ready batches exist. */}
          <button
            type="button"
            className={`batch-ready-chip ${readyOnly ? "batch-ready-chip--on" : ""}`}
            onClick={() => {
              setReadyOnly((v) => !v);
              setPage(0);
            }}
            data-testid="ready-for-keying-chip"
            aria-pressed={readyOnly}
            title="Show only batches whose docs are all ready for keying"
          >
            Ready for Keying
            <span className="batch-ready-chip-count">{readyCount}</span>
          </button>
          <input
            className="batch-search"
            type="text"
            placeholder="Search batches..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
          />
          <select
            className="batch-filter-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            data-testid="status-filter"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            className="batch-filter-select"
            value={jobTypeFilter}
            onChange={(e) => {
              setJobTypeFilter(e.target.value);
              setPage(0);
            }}
            data-testid="job-type-filter"
          >
            <option value="">All Job Types</option>
            {jobTypes.map((jt) => (
              <option key={jt.id} value={jt.id}>
                {jt.name}
              </option>
            ))}
          </select>
          {/* R-147 — Current Step filter chip. Client-side filter against the
              API's derived `currentStep.primary`. Mirrors the Status / Job
              Type dropdowns visually so it doesn't read as a new pattern. */}
          <select
            className="batch-filter-select"
            value={currentStepFilter}
            onChange={(e) => {
              setCurrentStepFilter(e.target.value);
              setPage(0);
            }}
            data-testid="current-step-filter"
            aria-label="Filter by current step"
          >
            {CURRENT_STEP_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <BulkActionBar
        selectedIds={selectedIds}
        onClear={() => setSelectedIds([])}
        onComplete={() => {
          setSelectedIds([]);
          refresh();
        }}
      />

      <div className="batch-list-container">
        {showInitialSkeleton && (
          <div className="admin-loading"><div className="loading-spinner" /></div>
        )}

        {/* R-182 — subtle non-blocking refresh indicator. Never unmounts the
            table or shifts layout; just a quiet cue that a background refetch
            is in flight (only after the first load has data). */}
        {isRefreshing && items.length > 0 && (
          <div className="batch-list-refreshing" role="status" aria-live="polite">
            Refreshing…
          </div>
        )}

        {error && <div className="admin-error">Error: {error}</div>}

        {!showInitialSkeleton && !error && items.length === 0 && (
          <EmptyState
            title="No batches yet"
            body="Upload your first batch or wait for the email ingestion channel to deliver one."
            action={{ label: "Upload batch", onClick: () => setUploadOpen(true) }}
          />
        )}

        {!showInitialSkeleton && !error && items.length > 0 && (
          <>
            <table className="batch-table" data-testid="batch-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input
                      type="checkbox"
                      checked={items.length > 0 && selectedIds.length === items.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedIds(items.map((b) => b.id));
                        } else {
                          setSelectedIds([]);
                        }
                      }}
                      aria-label="Select all batches"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                  <SortableHeader label="Batch" sortKey="label" sort={serverSort} onSort={onSort} />
                  <SortableHeader label="Source" sortKey="source" sort={serverSort} onSort={onSort} />
                  <SortableHeader label="Job Type" sortKey="jobType" sort={serverSort} onSort={onSort} />
                  <SortableHeader label="Status" sortKey="status" sort={serverSort} onSort={onSort} />
                  {/* R-147 — Current Step column. Header is clickable to
                      cycle asc → desc → none (default Ready-first order).
                      Sort runs client-side against the API's derived
                      `currentStep.primary`. */}
                  <th
                    className="batch-th-sortable"
                    onClick={() => {
                      setServerSort(null);
                      setCurrentStepSort((dir) =>
                        dir === null ? "asc" : dir === "asc" ? "desc" : null,
                      );
                    }}
                    data-testid="current-step-header"
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setServerSort(null);
                        setCurrentStepSort((dir) =>
                          dir === null ? "asc" : dir === "asc" ? "desc" : null,
                        );
                      }
                    }}
                    aria-sort={
                      currentStepSort === "asc"
                        ? "ascending"
                        : currentStepSort === "desc"
                          ? "descending"
                          : "none"
                    }
                  >
                    Current Step
                    {currentStepSort !== null && (
                      <span
                        className="batch-th-sort-indicator"
                        aria-hidden="true"
                      >
                        {currentStepSort === "asc" ? "▲" : "▼"}
                      </span>
                    )}
                  </th>
                  <SortableHeader label="Documents" sortKey="documents" sort={serverSort} onSort={onSort} />
                  <th>Progress</th>
                  <SortableHeader label="Created" sortKey="createdAt" sort={serverSort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {visibleItems.map((batch) => {
                  // R-191 — read the server-derived partial-credit value.
                  // The previous `completedCount / documentCount` calc was
                  // binary (exported docs only) and stuck at 0% for every
                  // mid-pipeline batch on the list view. Pre-R-191 API
                  // responses don't include the field; default to 0 so the
                  // UI degrades gracefully instead of rendering NaN%.
                  const pct = batch.progressPct ?? 0;
                  const isComplete = pct === 100;

                  return (
                    <tr
                      key={batch.id}
                      onClick={() => navigate(`/batches/${batch.id}`)}
                      data-testid="batch-row"
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(batch.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedIds((prev) => [...prev, batch.id]);
                            } else {
                              setSelectedIds((prev) => prev.filter((id) => id !== batch.id));
                            }
                          }}
                          aria-label={`Select ${formatBatchDisplayId(batch)}`}
                          data-testid="batch-checkbox"
                        />
                      </td>
                      <td>
                        {/* R-132 — primary identifier line. Structured displayIds
                            render in normal weight; legacy cuid-fallbacks render
                            muted so operators can tell at a glance which batches
                            predate the displayId rollout.
                            R-146 — if the tenant configured a batch-label
                            template, render that instead. Templates are
                            presentation-only; aria-label sticks with the
                            canonical displayId so screen-reader navigation
                            stays stable across template changes. */}
                        <span
                          className={
                            hasStructuredDisplayId(batch)
                              ? "batch-display-id"
                              : "batch-display-id batch-display-id--fallback"
                          }
                          data-testid="batch-display-id"
                        >
                          {renderBatchLabel(batch)}
                        </span>
                        {/* R-167 WU-7 — inline hint when a tenant runs templates
                            but THIS batch was created before the template (or
                            allocation failed). Helps operators understand why
                            a row shows the bare displayId instead of e.g.
                            `INV-2026-0042`. Only renders when the tenant has
                            at least one display template configured. */}
                        {batch.customDisplayLabel == null && hasAnyTemplate && (
                          <span
                            className="batch-display-id-info"
                            data-testid="batch-display-id-info"
                            title="Custom label not assigned (batch predates current template config)."
                            aria-label="Custom label not assigned"
                          >
                            ℹ
                          </span>
                        )}
                        <span
                          className="batch-name-sub"
                          data-testid="batch-name-sub"
                        >
                          {batch.name}
                        </span>
                        {/* R-133 — per-stage breakdown line under the batch
                            name, e.g. "5 ready · 0 scanning · 0 error". Lets
                            a keyer compare batches without drilling in. */}
                        <BatchStageBreakdown breakdown={batch.stageBreakdown} />

                      </td>
                      <td>
                        {batch.source === "cross-type-route" ? (
                          // F-CAPTURE-019 — created by the route-to-type
                          // automation; say so, and link the batch it came from.
                          batch.routedFromBatchId ? (
                            <Link className="batch-source" to={`/batches/${batch.routedFromBatchId}`} onClick={(e) => e.stopPropagation()}>
                              Routed
                            </Link>
                          ) : (
                            <span className="batch-source">Routed</span>
                          )
                        ) : (
                          <span className="batch-source">{batch.source}</span>
                        )}
                      </td>
                      <td>{batch.jobTypeName}</td>
                      <td>
                        {/* R-133 / F-074 — the badge renders ONE composed status
                            phrase ("<state> — <workability>") derived from the
                            doc breakdown + workability, replacing the raw
                            Batch.status display AND the separate work-item span
                            that used to sit next to it. */}
                        <BatchStatusBadge
                          breakdown={batch.stageBreakdown}
                          workability={workItemStatus(batch)}
                        />
                        {/* R-261 — rescan-requests badge: only shown when > 0. */}
                        {(batch.openRescanCount ?? 0) > 0 && (
                          <span className="batch-rescan-badge" title="Rescan requested">
                            Rescan ×{batch.openRescanCount}
                          </span>
                        )}
                        {batch.status === "needs_header_review" && (
                          <Link
                            to={`/batch-fix/${batch.id}`}
                            className="batch-fix-link"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Fix
                          </Link>
                        )}
                      </td>
                      {/* R-147 — Current Step cell. Renders the dominant
                          active step via formatStageLabel (shared with
                          StageCard / PipelineTab so spelling is consistent).
                          When docs span multiple steps, append "+N" and put
                          the full per-step breakdown in the title tooltip.
                          When no active step (all docs exported), show a
                          neutral dash so the row layout stays stable. */}
                      <td>
                        {batch.currentStep?.primary ? (
                          <span
                            className="batch-current-step"
                            data-testid="batch-current-step"
                            data-current-step={batch.currentStep.primary}
                            title={Object.entries(batch.currentStep.breakdown)
                              .sort(
                                (a, b) =>
                                  (PIPELINE_STEP_RANK[a[0]] ??
                                    Number.MAX_SAFE_INTEGER) -
                                  (PIPELINE_STEP_RANK[b[0]] ??
                                    Number.MAX_SAFE_INTEGER),
                              )
                              .map(
                                ([step, count]) =>
                                  `${formatStageLabel(step)}: ${count}`,
                              )
                              .join(" · ")}
                          >
                            {formatStageLabel(batch.currentStep.primary)}
                            {batch.currentStep.distinctSteps > 1 && (
                              <span className="batch-current-step-extra">
                                +{batch.currentStep.distinctSteps - 1}
                              </span>
                            )}
                          </span>
                        ) : (
                          <span
                            className="batch-current-step batch-current-step--none"
                            data-testid="batch-current-step"
                            data-current-step="none"
                            title="No active step — all documents exported"
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td>{batch.documentCount} document{batch.documentCount !== 1 ? "s" : ""}</td>
                      <td>
                        <div className="batch-progress">
                          <div className="batch-progress-bar">
                            <div
                              className={`batch-progress-fill ${isComplete ? "batch-progress-fill--complete" : ""}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <span className="batch-progress-text">{pct}%</span>
                        </div>
                      </td>
                      <td>
                        <span className="batch-date">{formatRelativeTime(batch.createdAt)}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="batch-pagination">
                <span>
                  Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
                </span>
                <div className="batch-pagination-controls">
                  <button
                    className="batch-page-btn"
                    disabled={page === 0}
                    onClick={() => setPage((p) => p - 1)}
                  >
                    Prev
                  </button>
                  <button
                    className="batch-page-btn"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <UploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </div>
  );
}
